/**
 * useVirtualBackgroundImages: 가상배경 사용자 이미지 서버 연동 훅
 *
 * - 서버에서 사용자 업로드 이미지 목록 조회 (GET /user/file_select)
 * - Object Storage에 이미지 업로드 + DB 메타데이터 저장 (PUT /user/file_uuid)
 * - 서버에서 이미지 삭제 (DELETE /user/file_delete)
 *
 * v1 원본: wehago-conference/src/RenewalComponents/Footer/SettingsDialog/BackgroundTab/BackgroundTabContainer.js
 */
import { useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { virtualBackgroundActions, type UserBackgroundImage } from '@/store/slices/virtualBackgroundSlice';
import apiManager from '@/api';
import { uploadFiles, viewObjectStorageFile } from '@/utils/objectStorage';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_USER_BG_MEMBER = 10;

export function useVirtualBackgroundImages() {
  const dispatch = useAppDispatch();
  const local = useAppSelector((s) => s.local);
  const { userImages, userImagesLoaded } = useAppSelector((s) => s.virtualBackground);
  const loadingRef = useRef(false);

  const portalId = local.userId;
  const roomId = local.roomId;
  const isGuest = local.isGuest;

  /**
   * 서버에서 사용자 업로드 이미지 목록 로드
   */
  const loadUserImages = useCallback(async () => {
    if (!portalId || isGuest || loadingRef.current) return;
    loadingRef.current = true;

    try {
      const uuidData = (await apiManager.selectBackgroundData(portalId)) as Array<{
        fileUuid: string;
        file_name: string;
        file_url: string;
      }>;

      if (!uuidData || !Array.isArray(uuidData) || uuidData.length === 0) {
        dispatch(virtualBackgroundActions.setUserImages([]));
        return;
      }

      const serviceKeyResult = await apiManager.getVideoServiceKey();
      const serviceKey = {
        service_key: serviceKeyResult.service_key,
        service_code: serviceKeyResult.service_code,
      };

      const newImages: UserBackgroundImage[] = [];

      for (const item of uuidData) {
        try {
          const fileInfo = await viewObjectStorageFile(item.fileUuid, serviceKey);
          if (fileInfo?.resultList?.[0]) {
            newImages.push({
              fileId: item.fileUuid,
              id: uuidv4(),
              src: fileInfo.resultList[0],
            });
          }
        } catch {
          // 개별 이미지 조회 실패 시 건너뜀
        }
      }

      dispatch(virtualBackgroundActions.setUserImages(newImages));
    } catch (err) {
      console.warn('[VBG] loadUserImages failed:', err);
      dispatch(virtualBackgroundActions.setUserImages([]));
    } finally {
      loadingRef.current = false;
    }
  }, [portalId, isGuest, dispatch]);

  /**
   * 이미지 업로드 (Object Storage + DB 저장)
   * @returns 업로드된 이미지 정보 또는 null
   */
  const uploadImage = useCallback(
    async (file: File): Promise<UserBackgroundImage | null> => {
      if (file.size > MAX_FILE_SIZE) return null;
      if (userImages.length >= MAX_USER_BG_MEMBER) return null;

      try {
        // 1) Object Storage에 업로드
        const uploadResult = await uploadFiles(file);
        if (!uploadResult) return null;

        const localBlobUrl = URL.createObjectURL(file);

        const newImage: UserBackgroundImage = {
          fileId: uploadResult.newFileName,
          id: uuidv4(),
          src: localBlobUrl,
        };

        // 2) DB에 메타데이터 저장
        if (portalId) {
          await apiManager.insertBackgroundData({
            roomId,
            portal_id: portalId,
            file_name: file.name,
            file_uuid: uploadResult.newFileName,
            file_url: uploadResult.thumbnail,
          });
        }

        // 3) Redux에 추가
        dispatch(virtualBackgroundActions.addUserImage(newImage));

        return newImage;
      } catch (err) {
        console.warn('[VBG] uploadImage failed:', err);
        return null;
      }
    },
    [portalId, roomId, userImages.length, dispatch],
  );

  /**
   * 이미지 삭제 (서버 + Redux)
   */
  const deleteImage = useCallback(
    async (clientId: string) => {
      const target = userImages.find((img) => img.id === clientId);
      if (!target) return;

      // Redux에서 즉시 제거
      dispatch(virtualBackgroundActions.removeUserImage(clientId));

      // 서버에서 삭제
      if (portalId) {
        try {
          await apiManager.deleteBackgroundData(roomId, portalId, target.fileId);
        } catch (err) {
          console.warn('[VBG] deleteImage failed:', err);
        }
      }
    },
    [portalId, roomId, userImages, dispatch],
  );

  return {
    userImages,
    userImagesLoaded,
    isGuest,
    loadUserImages,
    uploadImage,
    deleteImage,
  };
}
